"use client";
import React, { useState, useMemo, useEffect } from "react";
import {
  Laptop, X, Plus, Search, Edit2, Trash2, Eye,
  CreditCard, Wrench, ChevronDown, CheckCircle, AlertTriangle,
  Package, RotateCcw, Boxes, ShieldCheck, Clock,
  ChevronsLeft, ChevronsRight
} from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useEmployees } from "../../context/EmployeeContext";
import { useAssetType } from "../../context/AssetTypeContext";
import { useAssets } from "../../context/AssetContext";
import { useReturnAssets } from "../../context/ReturnAssetContext";
import { useAssetTickets } from "../../context/AssetTicketContext";
import { useAuth } from "../../context/AuthContext";

// ─── Shared helpers (kept identical to the Payroll module so both pages ───
//     feel like one product) ───────────────────────────────────────────────
const statusBadge = (status) => {
  const base = "px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap";
  if (["Returned", "Resolved", "Active", "Issued"].includes(status)) return `${base} bg-green-100 text-green-700`;
  if (["Pending", "In Progress", "Assigned"].includes(status)) return `${base} bg-yellow-100 text-yellow-700`;
  if (["Overdue", "Damaged", "Lost"].includes(status)) return `${base} bg-red-100 text-red-600`;
  return `${base} bg-gray-100 text-gray-600`;
};

const gradientBtn =
  "text-white px-4 py-2 rounded-lg text-sm font-medium transition-opacity hover:opacity-90";
const gradientStyle = { background: "linear-gradient(135deg, #2563eb, #d12465)" };

// Shared focus classes for lavender-themed inputs/selects/textareas
const focusRing = "focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb] transition";

// ─── Reusable pagination bar (same pattern used across the Payroll module) ─
function PaginationBar({ currentPage, setCurrentPage, rowsPerPage, setRowsPerPage, totalPages }) {
  return (
    <div className="flex flex-col sm:flex-row justify-between items-center gap-3 text-sm bg-white p-3 border-t border-gray-100">
      <div className="text-gray-500">
        Rows per page:
        <select
          className={`border rounded-md px-2 py-1 ml-1 ${focusRing}`}
          value={rowsPerPage}
          onChange={(e) => setRowsPerPage(Number(e.target.value))}
        >
          {[5, 10, 15].map((num) => (
            <option key={num} value={num}>
              {num}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center flex-wrap justify-center gap-1">
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronsLeft size={20} />
        </button>
        <button
          className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        >
          <MdKeyboardArrowLeft size={20} />
        </button>

        {(() => {
          let startPage = Math.max(currentPage - 1, 1);
          let endPage = startPage + 2;
          if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(endPage - 2, 1);
          }
          const pages = [];
          for (let i = startPage; i <= endPage; i++) {
            pages.push(
              <button
                key={i}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  i === currentPage
                    ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                onClick={() => setCurrentPage(i)}
              >
                {i}
              </button>
            );
          }
          return pages;
        })()}

        <button
          className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          <MdKeyboardArrowRight size={20} />
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronsRight size={20} />
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB 1 — ASSET INVENTORY (who has what)
// ════════════════════════════════════════════════════════════════════════════
function AssetInventory() {
  const { employees } = useEmployees();
  const { assetTypes } = useAssetType();
  const {assets, loading, createAsset, updateAsset, deleteAsset} = useAssets();
  const { canDo } = useAuth();

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [modal, setModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selected, setSelected] = useState(null);

  const [form, setForm] = useState({ empId: "", type: "", assetTag: "", brand: "", serial: "", issueDate: "", condition: "Good" });

  // ── Pagination (same pattern as Payroll module) ──
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const filtered = useMemo(() => {
  return assets.filter((a) => {
    const matchType =
      typeFilter === "All" ||
      a.asset_type_name === typeFilter;

    const matchSearch =
      a.asset_name?.toLowerCase().includes(search.toLowerCase()) ||
      a.asset_code?.toLowerCase().includes(search.toLowerCase()) ||
      a.username?.toLowerCase().includes(search.toLowerCase());

    return matchType && matchSearch;
  });
}, [assets, search, typeFilter]);

  // ── Pagination derived values ──
  const totalPages = Math.max(Math.ceil(filtered.length / rowsPerPage), 1);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filtered.slice(indexOfFirstRow, indexOfLastRow);

  // Reset to page 1 whenever the filters or page size change so the user
  // never lands on an empty page after narrowing the results.
  useEffect(() => {
    setCurrentPage(1);
  }, [search, typeFilter, rowsPerPage]);

const handleAdd = async (e) => {
  e.preventDefault();

  const user = JSON.parse(localStorage.getItem("user"));

  const payload = {
  user_id: form.empId,
  asset_name: form.brand,
  asset_type: form.type,
  asset_code: form.assetTag,
  date: form.issueDate,   // add this
  condition: form.condition,
  status: "assigned",
  create_audit_id: user.id,
};

  const response = await createAsset(payload);

  if (response.success) {

    setModal(false);

    setForm({
      empId: "",
      type: "",
      assetTag: "",
      brand: "",
      serial: "",
      issueDate: "",
      condition: "Good",
    });
  } else {
    alert(response.message);
  }
};

  const openEdit = (a) => {
    setSelected(a);
    setForm({
  type: a.asset_type,
  assetTag: a.asset_code,
  brand: a.asset_name,
  issueDate: a.date
    ? a.date.split(" ")[0]
    : "",
  condition: a.condition || "Good",
});
    setEditModal(true);
  };

  const handleEditSubmit = async (e) => {
  e.preventDefault();

  const user = JSON.parse(localStorage.getItem("user"));

 const payload = {
  asset_name: form.brand,
  asset_type: form.type,
  asset_code: form.assetTag,
  date: form.issueDate,
  condition: form.condition,
  update_audit_id: user.id,
};

  const response = await updateAsset(selected.id, payload);

  if (response.success) {
    setEditModal(false);
  } else {
    alert(response.message);
  }
};

  const totalAssets = assets.length;

const assignedCount = assets.filter(
  a => a.status?.toLowerCase() === "assigned"
).length;

const laptopCount = assets.filter(
  a => a.asset_type_name === "Laptop"
).length;

  const showActionsCol = canDo("Assets Inventory", "Edit") || canDo("Assets Inventory", "Delete");

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: "Total Assets",     value: totalAssets,   icon: <Boxes size={18}/>,    color: "text-blue-600 bg-blue-50" },
          { label: "Currently Issued", value: assignedCount, icon: <ShieldCheck size={18}/>, color: "text-green-600 bg-green-50" },
          { label: "Laptops in Use",   value: laptopCount,   icon: <Laptop size={18}/>,   color: "text-sky-600 bg-sky-50" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl p-4 flex items-center gap-3 shadow-sm border border-gray-100">
            <div className={`p-2 rounded-lg ${s.color}`}>{s.icon}</div>
            <div>
              <p className="text-xs text-gray-400">{s.label}</p>
              <p className="text-lg font-bold text-gray-800">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl py-3 px-4 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15}/>
          <input type="text" placeholder="Search by employee, asset tag, or brand..." value={search} onChange={e => setSearch(e.target.value)}
            className={`w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm ${focusRing}`}/>
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className={`border border-gray-300 rounded-lg px-3 py-2 text-sm w-full sm:w-40 shrink-0 ${focusRing}`}>
          <option>All</option>
          {assetTypes.filter(c => c.status === "active").map(type => (
            <option key={type.id} value={type.name}>
              {type.name}
            </option>
          ))}
        </select>
        {(search || typeFilter !== "All") && (
          <button
            type="button"
            onClick={() => { setSearch(""); setTypeFilter("All"); }}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
          >
            <X size={14} />
            Clear Filters
          </button>
        )}
        {canDo("Asset Inventory", "Add") && (

        <button
  onClick={() => {
    setForm({
      empId: "",
      type: "",
      assetTag: "",
      brand: "",
      serial: "",
      issueDate: "",
      condition: "Good",
    });
    setSelected(null);
    setModal(true);
  }}
  className={`${gradientBtn} flex items-center gap-1.5 justify-center sm:ml-auto shrink-0`}
  style={gradientStyle}
>
  <Plus size={16}/> Assign Asset
</button>
                  )}
      </div>

      {/* Table card — desktop table, mobile cards, and pagination all share one card */}
      <div className="bg-white shadow-md rounded-2xl overflow-hidden p-4">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-sky-50 text-gray-600">
              <tr>
                {[
                  "Employee",
                  "Asset Type",
                  "Asset Tag",
                  "Brand / Model",
                  "Issue Date",
                  "Condition",
                  ...(showActionsCol ? ["Actions"] : []),
                ].map(h => (
                  <th key={h} className="p-3 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentRows.length > 0 ? (
                currentRows.map((a, idx) => (
                  <tr key={a.id} className={`border-b hover:bg-gray-50 ${idx % 2 !== 0 ? "bg-[#f7f6fe]" : ""}`}>
                    <td className="p-3 text-gray-800">
                      {a.username}
                    </td>

                    <td className="p-3">
                      <span className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                        {a.asset_type_name}
                      </span>
                    </td>

                    <td className="p-3 text-gray-700 font-mono text-xs">
                      {a.asset_code}
                    </td>

                    <td className="p-3 text-gray-600">
                      {a.asset_name}
                    </td>

                    <td className="p-3 text-gray-600">
                      {a.date}
                    </td>

                    <td className="p-3">
                      <span className={statusBadge(
                        a.condition === "Good"
                          ? "Active"
                          : a.condition === "Fair"
                          ? "Pending"
                          : "Damaged"
                      )}>
                        {a.condition || "-"}
                      </span>
                    </td>

                    {showActionsCol && (
                      <td className="p-3">
                        <div className="flex gap-3">
                          {canDo("Assets Inventory", "Edit") && (
                            <button
                              onClick={() => openEdit(a)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Edit2 size={16} />
                            </button>
                          )}

                          {canDo("Assets Inventory", "Delete") && (
                            <button
                              onClick={() => {
                                setSelected(a);
                                setDeleteModal(true);
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={showActionsCol ? 7 : 6} className="text-center py-10 text-gray-400">
                    No assets found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-3 p-3">
          {currentRows.length > 0 ? currentRows.map(a => (
            <div key={a.id} className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex justify-between mb-2">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{a.username}</p>
                  <p className="text-xs text-gray-400">{a.asset_type_name} · {a.asset_name}</p>
                </div>
                <span className={statusBadge(a.condition === "Good" ? "Active" : a.condition === "Fair" ? "Pending" : "Damaged")}>{a.condition || "-"}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span className="font-mono">{a.asset_code}</span>
                <span>{a.date}</span>
              </div>
              <div className="flex justify-end gap-3 mt-2">
                {canDo("Assets Inventory", "Edit") && (
                  <button className="text-blue-600" onClick={() => openEdit(a)}><Edit2 size={15}/></button>
                )}
                {canDo("Assets Inventory", "Delete") && (
                  <button className="text-red-500" onClick={() => { setSelected(a); setDeleteModal(true); }}><Trash2 size={15}/></button>
                )}
              </div>
            </div>
          )) : (
            <div className="text-center py-10 text-gray-400">No assets found</div>
          )}
        </div>

        {/* Pagination — inside the same card as the table */}
        <PaginationBar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          totalPages={totalPages}
        />
      </div>

      {/* Add Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">Assign Asset</h3>
              <button onClick={() => setModal(false)}><X size={20} className="text-gray-500"/></button>
            </div>
            <form className="space-y-3" onSubmit={handleAdd}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
  <label className="block text-xs font-medium text-gray-600 mb-1">
    Employee
  </label>

  <select
    required
    value={form.empId}
    onChange={e =>
      setForm(f => ({
        ...f,
        empId: e.target.value,
        type: ""
      }))
    }
    className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}
  >
    <option value="">Select employee</option>

    {employees
      .filter(emp => emp.status?.toLowerCase() === "active")
      .map(emp => (
        <option key={emp.empId} value={emp.empId}>
          {emp.first_name} {emp.last_name}
        </option>
      ))}
  </select>
</div>

<div>
  <label className="block text-xs font-medium text-gray-600 mb-1">
    Asset Type
  </label>

  <select
    value={form.type}
    onChange={e =>
      setForm(f => ({
        ...f,
        type: e.target.value
      }))
    }
    className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}
    disabled={!form.empId}
  >
    <option value="">
      {!form.empId
        ? "Select Employee First"
        : assetTypes.filter(c => c.status === "active").length === 0
        ? "No asset types found"
        : "Select Asset Type"}
    </option>

    {form.empId &&
      assetTypes
        .filter(c => c.status === "active")
        .map(t => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
  </select>
</div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Asset Tag</label>
                  <input required type="text" placeholder="e.g. AST-LAP-1042" value={form.assetTag} onChange={e => setForm(f => ({...f, assetTag: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Brand / Model</label>
                  <input type="text" placeholder="e.g. Dell Latitude 5440" value={form.brand} onChange={e => setForm(f => ({...f, brand: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Serial Number</label>
                  <input type="text" placeholder="Optional" value={form.serial} onChange={e => setForm(f => ({...f, serial: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Issue Date</label>
                  <input required type="date" value={form.issueDate} onChange={e => setForm(f => ({...f, issueDate: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}/>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Condition</label>
                  <select value={form.condition} onChange={e => setForm(f => ({...f, condition: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}>
                    <option>Good</option><option>Fair</option><option>Damaged</option>
                  </select>
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
                <button type="button" onClick={() => setModal(false)} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50 w-full sm:w-auto">Cancel</button>
                <button
  type="submit"
  disabled={loading}
  className={`${gradientBtn} ${loading ? "opacity-60 cursor-not-allowed" : ""} w-full sm:w-auto`}
  style={gradientStyle}
>
  {loading ? "Assigning..." : "Assign Asset"}
</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">Edit Asset — {selected?.username}</h3>
              <button onClick={() => setEditModal(false)}><X size={20} className="text-gray-500"/></button>
            </div>
            <form className="space-y-3" onSubmit={handleEditSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Asset Type</label>
                  <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}>
                    {assetTypes.filter(c => c.status === "active").map(t => (
  <option key={t.id} value={t.id}>{t.name}</option>
))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Asset Tag</label>
                  <input type="text" value={form.assetTag} onChange={e => setForm(f => ({...f, assetTag: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Brand / Model</label>
                  <input type="text" value={form.brand} onChange={e => setForm(f => ({...f, brand: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Issue Date</label>
                  <input type="date" value={form.issueDate} onChange={e => setForm(f => ({...f, issueDate: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Condition</label>
                  <select value={form.condition} onChange={e => setForm(f => ({...f, condition: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}>
                    <option>Good</option><option>Fair</option><option>Damaged</option>
                  </select>
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
                <button type="button" onClick={() => setEditModal(false)} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50 w-full sm:w-auto">Cancel</button>
                <button
  type="submit"
  disabled={loading}
  className={`${gradientBtn} ${loading ? "opacity-60 cursor-not-allowed" : ""} w-full sm:w-auto`}
  style={gradientStyle}
>
  {loading ? "Saving..." : "Save Changes"}
</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-lg p-6 w-full sm:max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Remove Asset Record</h3>
            <p className="text-sm text-gray-500 mb-5">Remove <strong>{selected?.asset_type_name}</strong> ({selected?.asset_code}) assigned to <strong>{selected?.username}</strong>?</p>
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
              <button onClick={() => setDeleteModal(false)} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50 w-full sm:w-auto">Cancel</button>
            <button
  onClick={async () => {
    const user = JSON.parse(localStorage.getItem("user"));

    const response = await deleteAsset(
      selected.id,
      user.id
    );

    if (response.success) {
      setDeleteModal(false);
    } else {
      alert(response.message);
    }
  }}
  className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 w-full sm:w-auto"
>
  Delete
</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB 2 — MAINTENANCE & REPAIR
// ════════════════════════════════════════════════════════════════════════════
function AssetMaintenance() {
  const { employees } = useEmployees();
  const { canDo } = useAuth();
  const {
  tickets,
  loading,
  raiseTicket,
  approveTicket,
  deleteTicket,
} = useAssetTickets();
const { assets } = useAssets();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [modal, setModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selected, setSelected] = useState(null);

  // ── Pagination (same pattern as Payroll module) ──
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [form, setForm] = useState({
  empId: "",
  assetId: "",
  issue: "",
  priority: "Medium",
});

  const filtered = useMemo(() => {
  return tickets.filter((t) => {
    const matchStatus =
      filter === "All" ||
      t.status.toLowerCase() === filter.toLowerCase();

    const fullName = `${t.first_name} ${t.last_name}`;

    const matchSearch =
      fullName.toLowerCase().includes(search.toLowerCase()) ||
      t.asset_name.toLowerCase().includes(search.toLowerCase()) ||
      t.issue.toLowerCase().includes(search.toLowerCase());

    return matchStatus && matchSearch;
  });
}, [tickets, search, filter]);

  // ── Pagination derived values ──
  const totalPages = Math.max(Math.ceil(filtered.length / rowsPerPage), 1);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filtered.slice(indexOfFirstRow, indexOfLastRow);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filter, rowsPerPage]);

const handleAdd = async (e) => {
  e.preventDefault();

  const user = JSON.parse(localStorage.getItem("user"));

  const payload = {
    asset_id: form.assetId,
    user_id: form.empId,
    issue: form.issue,
    priority: form.priority.toLowerCase(),
    create_audit_id: user.id,
  };

  const response = await raiseTicket(payload);

  if (response.success) {
    setModal(false);

    setForm({
      empId: "",
      assetId: "",
      issue: "",
      priority: "Medium",
    });
  } else {
    alert(response.message);
  }
};

  const markResolved = (t) => {
    // setTickets(prev => prev.map(x =>
    //   x.id === t.id ? { ...x, status: "Resolved", resolvedDate: new Date().toISOString().slice(0, 10) } : x
    // ));
  };

  // Change ticket status from the view modal dropdown
  const handleViewStatusChange = async (newStatus) => {
  if (newStatus === selected.status) return;

  const user = JSON.parse(localStorage.getItem("user"));

  const response = await approveTicket(
    selected.id,
    newStatus,
    user.id
  );

  if (response.success) {

    setSelected((prev) => ({
      ...prev,
      status: newStatus,
    }));

    setViewModal(false);
  } else {
    alert(response.message);
  }
};

  const pendingCount = tickets.filter(
  t => t.status === "pending"
).length;

const approvedCount = tickets.filter(
  t => t.status === "approved"
).length;

const openCount = pendingCount;

  const priorityColor = {
    high:   "bg-red-50 text-red-600",
    medium: "bg-yellow-50 text-yellow-700",
    low:    "bg-gray-100 text-gray-600",
  };

  const showActionsCol = canDo("Asset Maintenance", "Edit") || canDo("Asset Maintenance", "Delete");

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
  {
    label: "Open Tickets",
    value: openCount,
    icon: <Wrench size={18} />,
    color: "text-yellow-600 bg-yellow-50",
  },
  {
    label: "Approved",
    value: approvedCount,
    icon: <CheckCircle size={18} />,
    color: "text-green-600 bg-green-50",
  },
  {
    label: "Total Tickets",
    value: tickets.length,
    icon: <Clock size={18} />,
    color: "text-blue-600 bg-blue-50",
  },
].map(s => (
          <div key={s.label} className="bg-white rounded-xl p-4 flex items-center gap-3 shadow-sm border border-gray-100">
            <div className={`p-2 rounded-lg ${s.color}`}>{s.icon}</div>
            <div>
              <p className="text-xs text-gray-400">{s.label}</p>
              <p className="text-lg font-bold text-gray-800">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl py-3 px-4 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15}/>
          <input
            type="text"
            placeholder="Search by employee, asset tag, or issue..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm ${focusRing}`}
          />
        </div>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className={`border border-gray-300 rounded-lg px-3 py-2 text-sm w-full sm:w-40 shrink-0 ${focusRing}`}
        >
          <option>All</option>
          <option>Pending</option>
          <option>In Progress</option>
          <option>Resolved</option>
        </select>
        {(search || filter !== "All") && (
          <button
            type="button"
            onClick={() => { setSearch(""); setFilter("All"); }}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
          >
            <X size={14} />
            Clear Filters
          </button>
        )}
        {canDo("Asset Maintenance", "Add") && (
        <button
          onClick={() => setModal(true)}
          className={`${gradientBtn} flex items-center gap-1.5 justify-center sm:ml-auto shrink-0`}
          style={gradientStyle}
        >
          <Plus size={16}/> Raise Repair Request
        </button>
        )}
      </div>

      {/* Table card — desktop table, mobile cards, and pagination all share one card */}
      <div className="bg-white shadow-md rounded-2xl overflow-hidden p-4">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-sky-50 text-gray-600">
              <tr>
                {[
                  "Employee",
                  "Asset Tag",
                  "Issue",
                  "Priority",
                  "Raised On",
                  "Status",
                  ...(showActionsCol ? ["Actions"] : []),
                ].map(h => (
                  <th key={h} className="p-3 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentRows.length > 0 ? currentRows.map((t, idx) => (
                <tr key={t.id} className={`border-b hover:bg-gray-50 ${idx % 2 !== 0 ? "bg-[#f7f6fe]" : ""}`}>
                  <td className="p-3 text-gray-800 whitespace-nowrap">{t.first_name} {t.last_name}</td>
                  <td className="p-3 text-gray-700 font-mono text-xs">{t.asset_name}</td>
                  <td className="p-3 text-gray-600 max-w-[220px] truncate">{t.issue}</td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityColor[t.priority]}`}>{t.priority}</span>
                  </td>
                  <td className="p-3 text-gray-600 whitespace-nowrap">{t.create_audit_time}</td>
                  <td className="p-3"><span className={statusBadge(
  t.status === "approved"
    ? "Resolved"
    : "Pending"
)}>
  {t.status}
</span></td>
                  {showActionsCol && (
  <td className="p-3">
    <div className="flex gap-3">

      {/* View/Edit */}
      {canDo("Asset Maintenance", "Edit") && (
        <button
          className="text-gray-600 hover:text-gray-800"
          title="Edit"
          onClick={() => {
            setSelected(t);
            setViewModal(true);
          }}
        >
          <Eye size={16}/>
        </button>
      )}

      {/* Delete */}
      {canDo("Asset Maintenance", "Delete") && (
        <button
          className="text-red-500 hover:text-red-700"
          title="Delete"
          onClick={() => {
            setSelected(t);
            setDeleteModal(true);
          }}
        >
          <Trash2 size={16}/>
        </button>
      )}

    </div>
  </td>
)}
                </tr>
              )) : (
                <tr><td colSpan={showActionsCol ? 7 : 6} className="text-center py-10 text-gray-400">No repair requests found</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-3 p-3">
          {currentRows.length > 0 ? currentRows.map(t => (
            <div key={t.id} className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex justify-between mb-2">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{t.first_name} {t.last_name}</p>
                  <p className="text-xs text-gray-400 font-mono">{t.asset_name}</p>
                </div>
                <span className={statusBadge(
  t.status === "approved"
    ? "Resolved"
    : "Pending"
)}>
  {t.status}
</span>
              </div>
              <p className="text-xs text-gray-600 mb-2">{t.issue}</p>
              <div className="flex items-center justify-between">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityColor[t.priority]}`}>{t.priority}</span>
                <div className="flex gap-3">
                  {canDo("Asset Maintenance", "Edit") && (
                    <button className="text-gray-600" onClick={() => { setSelected(t); setViewModal(true); }}><Eye size={15}/></button>
                  )}
                  {canDo("Asset Maintenance", "Delete") && (
                    <button className="text-red-500" onClick={() => { setSelected(t); setDeleteModal(true); }}><Trash2 size={15}/></button>
                  )}
                </div>
              </div>
            </div>
          )) : (
            <div className="text-center py-10 text-gray-400">No repair requests found</div>
          )}
        </div>

        {/* Pagination — inside the same card as the table */}
        <PaginationBar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          totalPages={totalPages}
        />
      </div>

      {/* ── Add Modal ── */}
      {modal && (
<div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">Raise Repair Request</h3>
              <button onClick={() => setModal(false)}><X size={20} className="text-gray-500"/></button>
            </div>
            <form className="space-y-3" onSubmit={handleAdd}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Employee</label>
                  <select required value={form.empId} onChange={e => setForm(f => ({...f, empId: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}>
                    <option value="">Select employee</option>
                    {employees.filter(emp => emp.status?.toLowerCase() === "active").map(emp => (
                      <option key={emp.empId} value={emp.empId}>{emp.first_name} {emp.last_name}</option>
                    ))}
                  </select>
                </div>
                <div>
  <label className="block text-xs font-medium text-gray-600 mb-1">
    Asset Tag
  </label>

  <select
    required
    value={form.assetId}
    onChange={(e) =>
      setForm((f) => ({
        ...f,
        assetId: e.target.value,
      }))
    }
    className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}
    disabled={!form.empId || assets.filter(
      asset =>
        asset.user_id?.toString() === form.empId.toString() &&
        asset.status?.toLowerCase() === "assigned"
    ).length === 0}
  >
    <option value="">
      {!form.empId
        ? "Select Employee First"
        : assets.filter(
            asset =>
              asset.user_id?.toString() === form.empId.toString() &&
              asset.status?.toLowerCase() === "assigned"
          ).length === 0
        ? "No assets found"
        : "Select Asset"}
    </option>

    {assets
      .filter(
        asset =>
          asset.user_id?.toString() === form.empId.toString() &&
          asset.status?.toLowerCase() === "assigned"
      )
      .map(asset => (
        <option key={asset.id} value={asset.id}>
          {asset.asset_name}
        </option>
      ))}
  </select>
</div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Issue Description</label>
                  <textarea required rows={3} placeholder="Describe what's wrong with the asset" value={form.issue} onChange={e => setForm(f => ({...f, issue: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm resize-none ${focusRing}`}/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                  <select value={form.priority} onChange={e => setForm(f => ({...f, priority: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}>
                    <option>Low</option><option>Medium</option><option>High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                  <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}>
                    <option>Pending</option><option>In Progress</option>
                  </select>
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
                <button type="button" onClick={() => setModal(false)} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50 w-full sm:w-auto">Cancel</button>
                <button
  type="submit"
  disabled={loading}
  className={`${gradientBtn} ${loading ? "opacity-60 cursor-not-allowed" : ""} w-full sm:w-auto`}
  style={gradientStyle}
>
  {loading ? "Submitting..." : "Submit Request"}
</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Modal (with inline status change) ── */}
      {viewModal && selected && (
<div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Repair Request Details</h3>
              <button onClick={() => setViewModal(false)}><X size={20} className="text-gray-500"/></button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 rounded-lg bg-yellow-50 text-yellow-600"><Wrench size={18}/></div>
                <p className="font-semibold text-gray-800">{selected.first_name} {selected.last_name}</p>
              </div>

              <p className="flex justify-between border-b border-gray-100 py-1.5">
                <span className="text-gray-400">Asset Tag</span>
                <span className="font-mono text-gray-800">{selected.asset_name}</span>
              </p>

              <p className="flex justify-between border-b border-gray-100 py-1.5">
                <span className="text-gray-400">Priority</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityColor[selected.priority]}`}>
                  {selected.priority}
                </span>
              </p>

              <p className="flex justify-between border-b border-gray-100 py-1.5">
                <span className="text-gray-400">Raised On</span>
                <span className="font-medium text-gray-800">{selected.create_audit_time}</span>
              </p>

              <p className="flex justify-between border-b border-gray-100 py-1.5">
                <span className="text-gray-400">Resolved On</span>
                <span className="font-medium text-gray-800">{selected.create_audit_time || "—"}</span>
              </p>

              {/* ── Status row with inline dropdown ── */}
              <div className="flex justify-between items-center border-b border-gray-100 py-1.5">
                <span className="text-gray-400">Status</span>
                <select
  value={selected.status}
  onChange={(e) => handleViewStatusChange(e.target.value)}
  className="border border-gray-300 rounded-lg px-2 py-1 text-xs"
>
  <option value="pending">Pending</option>
  <option value="inprogress">In Progress</option>
  <option value="approved">Approved</option>
</select>
              </div>

              <div className="pt-2">
                <p className="text-gray-400 mb-1">Issue</p>
                <p className="text-gray-700 bg-gray-50 rounded-lg p-3">{selected.issue}</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setViewModal(false)} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Modal ── */}
      {deleteModal && (
<div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-lg p-6 w-full sm:max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete Repair Request</h3>
            <p className="text-sm text-gray-500 mb-5">
              Remove the repair request for <strong>{selected?.asset_name}</strong> raised by <strong>{selected?.first_name} {selected?.last_name}</strong>?
            </p>
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
              <button onClick={() => setDeleteModal(false)} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50 w-full sm:w-auto">Cancel</button>
              <button
                onClick={async () => {
                  const user = JSON.parse(localStorage.getItem("user"));
                  const response = await deleteTicket(selected.id, user.id);
                  if (response.success) {
                    setDeleteModal(false);
                  } else {
                    alert(response.message);
                  }
                }}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 w-full sm:w-auto"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB 3 — ASSET RETURN / EXIT CLEARANCE
// (tracks whether each item issued to an exiting employee has come back —
//  this is the "am I returning that laptop/charger or not when I quit" tab)
// ════════════════════════════════════════════════════════════════════════════
function AssetReturn() {
  const { employees } = useEmployees();
  const { canDo } = useAuth();
  const { assets } = useAssets();
  const {
    assignedAssets,
    fetchAssignedAssets,
    returnAsset,
    loading
  } = useReturnAssets();

  useEffect(() => {
    fetchAssignedAssets();
  }, []);

  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);

  // ── Pagination (same pattern as Payroll module) ──
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [form, setForm] = useState({
    empId: "",
    exitDate: "",
    assetId: "",
    description: "",
  });

  const employeeAssets = useMemo(() => {
    if (!form.empId) return [];

    return assets.filter(
      (asset) =>
        asset.user_id?.toString() === form.empId.toString() &&
        asset.status?.toLowerCase() === "assigned"
    );
  }, [assets, form.empId]);

  const records = useMemo(() => {
    const grouped = {};

    assignedAssets.forEach(item => {
      const key = item.user_id;

      if (!grouped[key]) {
        grouped[key] = {
          id: key,
          empId: item.user_id,
          empName: `${item.first_name} ${item.last_name}`,
          exitDate: item.return_date || "",
          itemsMap: new Map(), // dedupe by asset_id — keep latest status per asset
        };
      }

      const rawStatus = (item.status || "").toString().trim().toLowerCase();

      // Some APIs return one row per status-change event rather than one row
      // per assigned asset, which duplicates the same asset_id multiple times.
      // Keying by asset_id and overwriting keeps only the latest entry.
      grouped[key].itemsMap.set(item.asset_id, {
        asset_id: item.asset_id,
        assetTag: item.asset_code,
        type: item.asset_name,
        status: rawStatus === "returned" ? "Returned" : "Pending",
      });

      // Track the most recent return_date seen for this employee's exit date display
      if (item.return_date && item.return_date > (grouped[key].exitDate || "")) {
        grouped[key].exitDate = item.return_date;
      }
    });

    return Object.values(grouped).map(r => ({
      id: r.id,
      empId: r.empId,
      empName: r.empName,
      exitDate: r.exitDate,
      items: Array.from(r.itemsMap.values()),
    }));
  }, [assignedAssets]);

  const filtered = records.filter(r => r.empName.toLowerCase().includes(search.toLowerCase()));

  // ── Pagination derived values ──
  const totalPages = Math.max(Math.ceil(filtered.length / rowsPerPage), 1);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filtered.slice(indexOfFirstRow, indexOfLastRow);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, rowsPerPage]);

  const clearanceFor = (r) => {
    const allReturned = r.items.every(i => i.status === "Returned");
    return allReturned ? "Returned" : "Pending";
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    const payload = {
      user_id: form.empId,
      asset_id: form.assetId,
      return_date: form.exitDate,
      description: form.description,
    };

    const res = await returnAsset(payload);

    if (res.success) {
      setModal(false);
      fetchAssignedAssets();

      setForm({
        empId: "",
        exitDate: "",
        assetId: "",
        description: "",
      });
    } else {
      alert(res.message);
    }
  };

  const pendingClearance = records.filter(r => clearanceFor(r) === "Pending").length;

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: "Exits Awaiting Clearance", value: pendingClearance,   icon: <AlertTriangle size={18}/>, color: "text-red-600 bg-red-50" },
          { label: "Exit Records",             value: records.length,     icon: <Package size={18}/>,       color: "text-blue-600 bg-blue-50" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl p-4 flex items-center gap-3 shadow-sm border border-gray-100">
            <div className={`p-2 rounded-lg ${s.color}`}>{s.icon}</div>
            <div>
              <p className="text-xs text-gray-400">{s.label}</p>
              <p className="text-lg font-bold text-gray-800">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl py-3 px-4 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15}/>
          <input type="text" placeholder="Search by employee name..." value={search} onChange={e => setSearch(e.target.value)}
            className={`w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm ${focusRing}`}/>
        </div>
        {canDo("Asset Return", "Add") && (
        <button onClick={() => setModal(true)} className={`${gradientBtn} flex items-center gap-1.5 justify-center sm:ml-auto shrink-0`} style={gradientStyle}>
          <Plus size={16}/> Start Exit Clearance
        </button>
        )}
      </div>

      {/* Exit-record cards, wrapped so pagination sits inside the same card group */}
      <div className="bg-white shadow-md rounded-2xl overflow-hidden">
        <div className="space-y-3 p-3">
          {currentRows.length > 0 ? currentRows.map(r => {
            const clearance = clearanceFor(r);
            const returnedCount = r.items.filter(i => i.status === "Returned").length;
            return (
              <div key={r.id} className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{r.empName}</p>
                    <p className="text-xs text-gray-400">Exit date: {r.exitDate || "—"} · {returnedCount}/{r.items.length} items returned</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={statusBadge(clearance)}>{clearance === "Returned" ? "Cleared" : "Pending Clearance"}</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {r.items.map(i => (
                    <div key={i.assetTag} className="flex items-center justify-between gap-2 border border-gray-100 rounded-lg px-3 py-2">
                      <span className="flex items-center gap-2 text-sm min-w-0">
                        <input
                          type="checkbox"
                          checked={i.status === "Returned"}
                          readOnly
                          className="accent-blue-600"
                          disabled
                        />
                        <span className="font-mono text-xs text-gray-600 truncate">{i.assetTag}</span>
                        <span className="text-xs text-gray-400 truncate">({i.type})</span>
                      </span>
                      <span className={statusBadge(i.status)}>{i.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          }) : <div className="text-center py-10 text-gray-400">No exit clearance records found</div>}
        </div>

        {/* Pagination — inside the same card as the exit records */}
        <PaginationBar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          totalPages={totalPages}
        />
      </div>

      {/* Add Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">Start Exit Clearance</h3>
              <button onClick={() => setModal(false)}><X size={20} className="text-gray-500"/></button>
            </div>
            <form className="space-y-3" onSubmit={handleAdd}>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Employee</label>
                <select required value={form.empId} onChange={e => setForm(f => ({...f, empId: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}>
                  <option value="">Select employee</option>
                  {employees.filter(emp => emp.status?.toLowerCase() === "active").map(emp => (
                    <option key={emp.empId} value={emp.empId}>
                      {emp.first_name} {emp.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Select Asset
                </label>

                <select
                  required
                  value={form.assetId}
                  onChange={(e) =>
                    setForm(f => ({
                      ...f,
                      assetId: e.target.value,
                    }))
                  }
                  className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}
                  disabled={!form.empId || employeeAssets.length === 0}
                >
                  <option value="">
                    {!form.empId
                      ? "Select Employee First"
                      : employeeAssets.length === 0
                      ? "No assets found"
                      : "Select Asset"}
                  </option>

                  {employeeAssets
                    .filter(asset => asset.status?.toLowerCase() !== "returned")
                    .map(asset => (
                      <option key={asset.id} value={asset.id}>
                        {asset.asset_name} ({asset.asset_code})
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Last Working / Exit Date</label>
                <input required type="date" value={form.exitDate} onChange={e => setForm(f => ({...f, exitDate: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}/>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Description
                </label>

                <textarea
                  rows={3}
                  placeholder="Enter return notes or condition details..."
                  value={form.description}
                  onChange={(e) =>
                    setForm(f => ({
                      ...f,
                      description: e.target.value,
                    }))
                  }
                  className={`border border-gray-300 rounded-lg p-2 w-full text-sm resize-none ${focusRing}`}
                />
              </div>

              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
                <button type="button" onClick={() => setModal(false)} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50 w-full sm:w-auto">Cancel</button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`${gradientBtn} ${loading ? "opacity-60 cursor-not-allowed" : ""} w-full sm:w-auto`}
                  style={gradientStyle}
                >
                  {loading ? "Creating..." : "Create Checklist"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ROOT — Tab shell (mirrors PayrollModule's tab pattern exactly)
// ════════════════════════════════════════════════════════════════════════════
export default function AssetManagementModule() {
  const [tab, setTab] = useState("inventory");

  const tabs = [
    { id: "inventory",   label: "Asset Inventory",      icon: <Boxes size={16}/> },
    { id: "maintenance", label: "Repair & Maintenance", icon: <Wrench size={16}/> },
    { id: "returns",     label: "Asset Return",         icon: <RotateCcw size={16}/> },
  ];

  return (
    <div className="bg-sky-50 min-h-screen">
      <div className="px-4 sm:px-6 lg:px-9 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Asset Management</h1>
          <p className="text-sm text-gray-400 mt-0.5">Track who has what, log repairs, and clear assets on exit</p>
        </div>

        {/* Tab bar */}
        <div className="bg-white rounded-xl p-1 flex gap-1 mb-6 shadow-sm overflow-x-auto">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex-1 justify-center ${
                tab === t.id
                  ? "text-white shadow-sm"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
              style={tab === t.id ? gradientStyle : {}}
            >
              {t.icon}
              <span className="hidden sm:inline">{t.label}</span>
              <span className="sm:hidden">{t.label.split(" ")[0]}</span>
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === "inventory"   && <AssetInventory/>}
        {tab === "maintenance" && <AssetMaintenance/>}
        {tab === "returns"     && <AssetReturn/>}
      </div>
    </div>
  );
}

export { AssetInventory, AssetMaintenance, AssetReturn };